import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

const CART_REWARDS_GIFT_DISMISSED_STORAGE_KEY = 'cart-rewards:gifts:dismissed';


/** @returns {Record<string, boolean>} */
function readDismissedGifts() {
  try {
    const raw = window.sessionStorage.getItem(CART_REWARDS_GIFT_DISMISSED_STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * @param {string} variantId
 * @param {boolean} dismissed
 */
function setGiftDismissed(variantId, dismissed) {
  if (!variantId) return;

  const map = readDismissedGifts();
  if (dismissed) {
    map[variantId] = true;
  } else {
    delete map[variantId];
  }

  try {
    window.sessionStorage.setItem(CART_REWARDS_GIFT_DISMISSED_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Ignore storage failures (private mode / quota)
  }
}

/** @param {string} variantId */
function isGiftDismissed(variantId) {
  if (!variantId) return false;
  return Boolean(readDismissedGifts()[variantId]);
}

/**
 * @param {number} subtotalInMinorUnits
 * @param {number} thresholdInMinorUnits
 * @param {string} operator
 * @returns {boolean}
 */
function matchesGiftCondition(subtotalInMinorUnits, thresholdInMinorUnits, operator) {
  switch (operator) {
    case '>':
      return subtotalInMinorUnits > thresholdInMinorUnits;
    case '>=':
      return subtotalInMinorUnits >= thresholdInMinorUnits;
    default:
      return subtotalInMinorUnits >= thresholdInMinorUnits;
  }
}

/**
 * @param {any} cartRewardsConfig
 * @returns {Array<{variantId: string, operator: string, threshold: number}>}
 */
function getGiftRules(cartRewardsConfig) {
  if (!Array.isArray(cartRewardsConfig?.giftRules)) return [];

  return cartRewardsConfig.giftRules
    .map((rule) => {
      const variantId = rule?.variantId ? String(rule.variantId) : '';
      const operator = typeof rule?.operator === 'string' ? rule.operator : '>=';
      const threshold = Number(rule?.threshold);

      return { variantId, operator, threshold };
    })
    .filter((rule) => rule.variantId && Number.isFinite(rule.threshold) && rule.threshold >= 0);
}

/**
 * @param {{threshold: number}} rule
 * @returns {number}
 */
function getGiftThresholdInMinorUnits(rule) {
  return Math.round(Number(rule.threshold));
}

/** @returns {any} */
function readCartRewardsGlobalConfig() {
  const el = document.querySelector('script[data-cart-rewards-global-config]');
  if (!el) {
    return null;
  }

  try {
    const config = JSON.parse(el.textContent || '');
    return config;
  } catch (error) {
    return null;
  }
}

/**
 * @param {any} cartRewardsConfig
 * @param {Array<{variantId: string, operator: string, threshold: number}>} giftRules
 * @param {any} cart
 * @returns {string}
 */
function getGiftSkipReason(cartRewardsConfig, giftRules, cart) {
  if (!cartRewardsConfig) return 'missing global config';
  if (!cartRewardsConfig.enabled) return 'settings.enable_cart_rewards is false';
  if (!cartRewardsConfig.audienceAllowed) return 'audienceAllowed is false';
  if (!cartRewardsConfig.giftEnabled) return 'giftEnabled is false';
  if (giftRules.length === 0) return 'no valid gift rules';
  if (!Array.isArray(cart?.items)) return 'cart payload has no items array';

  return '';
}

/**
 * @param {any} response
 * @returns {Promise<any>}
 */
async function enrichCartAddResponseWithCart(response) {
  if (!response?.sections) return response;

  try {
    const cart = await fetch(`${Theme.routes.cart_url}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((cartResponse) => (cartResponse.ok ? cartResponse.json() : null));

    if (!cart?.items) return response;

    return {
      ...cart,
      sections: response.sections,
    };
  } catch {
    return response;
  }
}

/**
 * @param {any} cart
 * @param {string[]} sectionIds
 * @returns {Promise<any>}
 */
async function syncCartRewardsWithCart(cart, sectionIds) {
  const cartRewardsConfig = readCartRewardsGlobalConfig();
  const giftRules = getGiftRules(cartRewardsConfig);
  const giftSkipReason = getGiftSkipReason(cartRewardsConfig, giftRules, cart);
  const shouldEvaluateGift = giftSkipReason === '';

  if (!shouldEvaluateGift) return null;

  const giftVariantIds = new Set(giftRules.map((rule) => rule.variantId));
  let subtotalExcludingGift = 0;
  /** @type {Map<string, any[]>} */
  const giftLinesByVariantId = new Map();

  for (const item of cart.items) {
    const itemVariantId = String(item.variant_id);
    if (giftVariantIds.has(itemVariantId)) {
      const lines = giftLinesByVariantId.get(itemVariantId) || [];
      lines.push(item);
      giftLinesByVariantId.set(itemVariantId, lines);
      continue;
    }

    subtotalExcludingGift += item.final_line_price;
  }

  const desiredGiftVariantIds = new Set();
  const ruleResults = [];
  for (const rule of giftRules) {
    const thresholdInMinorUnits = getGiftThresholdInMinorUnits(rule);
    const shouldHaveGift = matchesGiftCondition(subtotalExcludingGift, thresholdInMinorUnits, rule.operator);
    const giftLines = giftLinesByVariantId.get(rule.variantId) || [];

    ruleResults.push({
      variantId: rule.variantId,
      operator: rule.operator,
      threshold: rule.threshold,
      thresholdInMinorUnits,
      shouldHaveGift,
      giftLines: giftLines.length,
      dismissed: isGiftDismissed(rule.variantId),
    });

    if (!shouldHaveGift || giftLines.length > 0) {
      setGiftDismissed(rule.variantId, false);
    }

    if (shouldHaveGift) {
      desiredGiftVariantIds.add(rule.variantId);
    }
  }

  const sections = sectionIds.join(',');

  for (const variantId of giftVariantIds) {
    const giftLines = giftLinesByVariantId.get(variantId) || [];
    const shouldHaveGift = desiredGiftVariantIds.has(variantId);

    if (shouldHaveGift && giftLines.length === 0) {
      if (isGiftDismissed(variantId)) {
        continue;
      }

      const payload = {
        items: [{
          id: Number(variantId),
          quantity: 1,
        }],
        sections,
        sections_url: window.location.pathname,
      };
      const body = JSON.stringify(payload);


      return fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }))
        .then(async (response) => {
          const parsedResponse = response.ok ? await response.json() : null;
          if (!response.ok) return null;
          return enrichCartAddResponseWithCart(parsedResponse);
        })
        .catch((error) => {
          return null;
        });
    }

    if (!shouldHaveGift && giftLines.length > 0) {
      /** @type {Record<string, number>} */
      const updates = {};
      giftLines.forEach((giftLine) => {
        updates[giftLine.key] = 0;
      });

      const payload = {
        updates,
        sections,
        sections_url: window.location.pathname,
      };
      const body = JSON.stringify(payload);

      return fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
        .then(async (response) => {
          const parsedResponse = response.ok ? await response.json() : null;
          return parsedResponse;
        })
        .catch((error) => {
          return null;
        });
    }

    if (shouldHaveGift && giftLines.length > 0) {
      const [firstGiftLine, ...extraGiftLines] = giftLines;
      const needsQtyFix = firstGiftLine.quantity !== 1 || extraGiftLines.length > 0;
      if (!needsQtyFix) continue;

      /** @type {Record<string, number>} */
      const updates = {
        [firstGiftLine.key]: 1,
      };
      extraGiftLines.forEach((giftLine) => {
        updates[giftLine.key] = 0;
      });

      const payload = {
        updates,
        sections,
        sections_url: window.location.pathname,
      };
      const body = JSON.stringify(payload);

      return fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
        .then(async (response) => {
          const parsedResponse = response.ok ? await response.json() : null;
          return parsedResponse;
        })
        .catch((error) => {
          return null;
        });
    }
  }

  return null;
}

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const row = this.refs.cartItemRows[line - 1];
    if (row?.hasAttribute('data-cart-rewards-gift')) {
      const giftVariantId = row.querySelector('[data-cpcproducturl]')?.getAttribute('data-cpcproducturl') || '';
      if (giftVariantId) {
        setGiftDismissed(giftVariantId, true);
      }
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = row;

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        if (parsedResponseText.errors) {
          resetShimmer(this);
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const syncedCartResponse = await syncCartRewardsWithCart(parsedResponseText, Array.from(sectionsToUpdate));
        const finalResponse = syncedCartResponse || parsedResponseText;

        resetShimmer(this);

        const newSectionHTML = new DOMParser().parseFromString(
          finalResponse.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(finalResponse);

        this.dispatchEvent(
          new CartUpdateEvent(finalResponse, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: finalResponse.sections,
          })
        );

        morphSection(this.sectionId, finalResponse.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
